import React from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, Sparkle } from "lucide-react";

const CHART =
  "https://customer-assets.emergentagent.com/job_landing-forge-60/artifacts/zlwqwji5_OVERSIZED%20T%20SHIRT%20COLOUR%20CHART.png";

const tiles = [
  {
    url: "https://customer-assets.emergentagent.com/job_landing-forge-60/artifacts/93njo2nr_OVERSIZED%20T%20SHIRT%20%286%29.jpg",
    name: "Aqua",
    swatch: "#A8E6D5",
    fg: "#0A0A0A",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_landing-forge-60/artifacts/gbx7bg8x_OVERSIZED%20T%20SHIRT.jpg",
    name: "Lavender",
    swatch: "#C9B6E8",
    fg: "#0A0A0A",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_landing-forge-60/artifacts/wn3r3mup_OVERSIZED%20T%20SHIRT%20%285%29.jpg",
    name: "Sapphire Blue",
    swatch: "#3F7D94",
    fg: "#FFFFFF",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_landing-forge-60/artifacts/h9vn6x8y_OVERSIZED%20T%20SHIRT.jpeg",
    name: "Coffee",
    swatch: "#5C3A2E",
    fg: "#FFFFFF",
  },
];

const specs = [
  "180 GSM",
  "Super Soft Cotton",
  "Breathable",
  "Drop Shoulder",
  "11 Modern Shades",
  "Sizes M – XXL",
];

export default function Oversized() {
  return (
    <section
      id="oversized"
      className="border-b-4 border-black py-20 lg:py-28 bg-[#F0F0F0]"
      data-testid="oversized-section"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
        {/* Header */}
        <div className="grid grid-cols-12 gap-8 mb-12 lg:mb-16">
          <div className="col-span-12 lg:col-span-7">
            <div className="font-heading uppercase text-xs tracking-[0.3em] text-[#555] mb-4">
              /// Oversized · Drop-shoulder
            </div>
            <h2 className="font-heading uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Big tee energy.
              <br />
              <span className="bg-[#00F0FF] nb-border-2 px-2 inline-block">11 modern shades.</span>
            </h2>
            <p className="mt-5 max-w-xl text-base md:text-lg leading-relaxed text-[#0A0A0A]/80">
              The everyday essential — heavier feel, dropped shoulders, boxy
              hem. Premium 180 GSM combed cotton with a clean aesthetic that
              prints like a dream. Ideal for clothing brands, college
              merchandise, fashion labels and creator drops. Compatible with
              DTF, puff print and sublimation.
            </p>
          </div>

          <div className="col-span-12 lg:col-span-5 flex flex-wrap items-end gap-2 content-end">
            {specs.map((s) => (
              <span
                key={s}
                className="inline-flex items-center gap-1.5 nb-border-2 bg-white px-3 py-1.5 font-heading uppercase text-xs tracking-wider"
                data-testid={`oversized-spec-${s.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Check size={12} strokeWidth={3} className="text-[#FF0055]" />
                {s}
              </span>
            ))}
          </div>
        </div>

        {/* Branded colour chart hero */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="relative nb-border-4 nb-shadow-lg overflow-hidden bg-white"
          data-testid="oversized-colour-chart"
        >
          <div className="absolute top-4 left-4 z-10 inline-flex items-center gap-2 bg-[#FF0055] text-white nb-border-2 px-3 py-1.5 font-heading uppercase text-xs tracking-wider">
            <Sparkle size={12} strokeWidth={3} /> Premium · Everyday Essential
          </div>
          <img
            src={CHART}
            alt="Customade oversized t-shirt colour chart — 11 modern shades, 180 GSM"
            className="w-full h-auto block"
            loading="lazy"
          />
        </motion.div>

        {/* Hanger tiles */}
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {tiles.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="nb-tile nb-border-4 nb-shadow-sm bg-white aspect-[3/4]"
              data-testid={`oversized-tile-${i}`}
            >
              <img
                src={t.url}
                alt={`Oversized t-shirt in ${t.name}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div
                className="absolute bottom-3 left-3 right-3 nb-border-2 px-3 py-2 font-heading uppercase text-xs tracking-wider"
                style={{ background: t.swatch, color: t.fg }}
              >
                {t.name}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 nb-border-4 bg-[#00F0FF] text-[#0A0A0A] p-5 md:p-7">
          <div>
            <div className="font-heading uppercase text-xs tracking-[0.3em] text-[#FF0055] mb-1">
              Streetwear &amp; drops
            </div>
            <div className="font-heading uppercase text-xl md:text-2xl leading-tight">
              MOQ 25 pcs · DTF / puff print / sublimation ready.
            </div>
          </div>
          <a href="#contact" className="nb-btn nb-btn-magenta" data-testid="oversized-cta">
            Quote my drop <ArrowRight size={18} strokeWidth={3} />
          </a>
        </div>
      </div>
    </section>
  );
}
